import { t } from '@lingui/macro';

import EXPERIMENTS from '@rover/react-lib/src/constants/experiments.constants';
import type {
  ActionCreator,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { selectors as experimentsSelectors } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import {
  effects as petsEffects,
  selectors as petsSelectors,
} from '@rover/react-lib/src/redux/ducks/pets.duck';
import { selectors as serviceTypeSelectors } from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { getCookie } from '@rover/rsdk/src/modules/Network/cookies';
import LOCAL_STORAGE_KEYS from '@rover/shared/js/constants/localStorage.constants';
import { DROP_OFF_PICK_UP_SUPPORTED_SERVICES } from '@rover/shared/js/constants/searchPage.constants';
import { SearchPageDaycareTypeRequiredError } from '@rover/shared/js/search/utilities/analyticsEvents';
import filtersToQuery from '@rover/shared/js/search/utilities/filtersToQuery';
import { convertToSearchApiKeys } from '@rover/shared/js/search/utilities/searchFiltersMap';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import { Pet, PetType } from '@rover/types/src/Pet';
import { SelectOption } from '@rover/types/src/SelectOption';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';
import { ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';
import fireCrossPageEvent from '@rover/utilities/fireCrossPageEvent';

import homepageSearchFiltersFactory from './homepageSearchFiltersFactory';

export interface State {
  filters: HomepageSearchFilters;
  hasError: boolean;
}

type Actions = {
  updateFilters: ActionCreator<Partial<HomepageSearchFilters>>;
  setHasFilterError: ActionCreator<boolean>;
};

type Effects = {
  fireHomepageSearchEvent: EffectCreator<HomepageSearchFilters>;
  submit: EffectCreator<void>;
  fetchPetsAndUpdateFilters: EffectCreator<void>;
};

type Selectors = {
  getDateRangePickerLabel: SelectorCreator<string>;
  getFilters: SelectorCreator<HomepageSearchFilters>;
  getPetTypeBrowsableServiceTypeOptions: SelectorCreator<ServiceTypeOption[]>;
  getPetTypeServiceTypeOptions: SelectorCreator<ServiceTypeOption[]>;
  getServiceType: SelectorCreator<ServiceType>;
  getStartDatePlaceholder: SelectorCreator<string>;
  getEndDatePlaceholder: SelectorCreator<string>;
  getIsDaycareFilterEarlierInExperiment: SelectorCreator<boolean>;
  getIsDaycareFilterEarlierExperimentRequired: SelectorCreator<boolean>;
  getIsDaycareFilterOutsideMarketsInExperiment: SelectorCreator<boolean>;
  getisInBoardingFilterEarlierExperiment: SelectorCreator<boolean>;
  getHasFilterError: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'searchBox',
  initialState: {
    filters: homepageSearchFiltersFactory(),
    hasError: false,
  },
  actions: {
    updateFilters: (filtersToUpdate) => (state) => {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...filtersToUpdate,
        },
      };
    },
    setHasFilterError: (hasError) => (state) => {
      return {
        ...state,
        hasError,
      };
    },
  },

  effects: {
    fireHomepageSearchEvent: (filters: HomepageSearchFilters) => () => {
      // this format matches the original impl in homepage-vue.js/fireSearchAnalyticsEvent
      const formatSearchEventDate = (date: Date | null | undefined): string | null => {
        if (!date) return null;
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      };
      const dogSize = ['smallDogs', 'mediumDogs', 'largeDogs', 'giantDogs']
        .filter((i) => filters[i])
        .map((i) => i.replace('Dogs', ''));
      const event = {
        event: 'search-homepage',
        zipCode: filters.location,
        serviceType: filters.serviceTypeSlug,
        dogSize,
        dropOffDate: formatSearchEventDate(filters.dateRange.startDate),
        pickUpDate: formatSearchEventDate(filters.dateRange.endDate),
      };

      fireCrossPageEvent(LOCAL_STORAGE_KEYS.HOMEPAGE_SEARCH_EVENT, event);
    },
    submit:
      () =>
      async (
        dispatch,
        getState,
        { setHasFilterError },
        { fireHomepageSearchEvent },
        {
          getFilters,
          getHasFilterError,
          getIsDaycareFilterEarlierExperimentRequired,
          getIsDaycareFilterOutsideMarketsInExperiment,
          getServiceType,
        }
      ) => {
        const state = getState();
        dispatch(setHasFilterError(false));

        const filters = getFilters(state);
        const serviceType = getServiceType(state);
        const isDirty = true;
        const language = frontendConfigurationSelectors.getLanguage(state);
        const { dogCount, catCount, puppyCount, ...legacyFilters } = filters;

        // Desktop homepage has pet type checkboxes that need to be converted to pet counts, hence petType.includes... Mobile web uses dogCount and catCount instead.
        let legacyDogCount;
        if (
          legacyFilters.petType.includes(PetType.DOG) &&
          !legacyFilters.petType.includes(PetType.CAT)
        ) {
          legacyDogCount = 0;
        } else if (
          legacyFilters.petType?.includes(PetType.DOG) &&
          legacyFilters.petType.includes(PetType.CAT)
        ) {
          legacyDogCount = 1;
        }
        const legacyCatCount = legacyFilters.petType.includes(PetType.CAT) ? 1 : 0;

        const withRenamedServiceType = {
          ...legacyFilters,
          serviceType: filters.serviceTypeSlug,
          dogCount: dogCount || legacyDogCount,
          catCount: catCount || legacyCatCount,
          puppyCount: legacyFilters.petType.includes(PetType.PUPPY) ? puppyCount : 0,
        };
        const camelParams = filtersToQuery<HomepageSearchFilters>(
          withRenamedServiceType,
          serviceType,
          isDirty,
          language
        );
        const snakeParams = convertToSearchApiKeys({ filters: camelParams });
        const withEmptyLocation = { ...snakeParams, location: snakeParams.location || '' };

        const queryParams = Object.entries(withEmptyLocation).reduce(
          (acc, p) => `${acc}${acc.length ? '&' : ''}${p[0]}=${p[1]}`,
          ''
        );

        const daycareTypeFieldRequired =
          getIsDaycareFilterEarlierExperimentRequired(getState()) ||
          getIsDaycareFilterOutsideMarketsInExperiment(getState());
        // check to see if service is doggy-daycare and if daycare type is a 'required' field
        // if so, throw error if nothing is selected
        if (
          serviceType.slug === ServiceTypeSlug.DOGGY_DAY_CARE &&
          daycareTypeFieldRequired &&
          !filters.inSittersHome &&
          !filters.atDaycareFacility
        ) {
          dispatch(setHasFilterError(true));
          emitAnalyticsEvent(new SearchPageDaycareTypeRequiredError());
        }

        await dispatch(fireHomepageSearchEvent(filters));
        if (getHasFilterError(getState())) return;

        window.location.href = `/search/?${queryParams}`; // eslint-disable-line rover/no-platform-specific-globals-or-imports
      },

    fetchPetsAndUpdateFilters:
      () =>
      async (dispatch, getState, { updateFilters }) => {
        // don't fetch unless the user is authed
        if (!getCookie('rli')) return;

        await dispatch(petsEffects.fetchPets());

        const pets = petsSelectors.getPets(getState());
        if (pets.length) {
          const petTypes: PetType[] = pets.map((i: Pet) => i.petType);
          const filtersWithPetsSet: {
            pet: string[];
            petType: PetType[];
            serviceTypeSlug?: ServiceTypeSlug;
          } = {
            pet: pets.map((i: Pet) => i.pk.toString()),
            petType: [...new Set(petTypes)],
            serviceTypeSlug: petTypes.every((type) => type === PetType.CAT)
              ? ServiceTypeSlug.DROP_IN
              : ServiceTypeSlug.OVERNIGHT_BOARDING,
          };
          dispatch(updateFilters(filtersWithPetsSet));
        }
      },
  },

  selectors: {
    getFilters: (getState, createSelector) => createSelector([getState], (s) => s.filters),
    getServiceType: (getState, createSelector) =>
      createSelector(
        [getState, serviceTypeSelectors.getServiceTypeBySlug],
        (x, serviceTypeBySlug: (serviceTypeSlug: ServiceTypeSlug) => ServiceType) => {
          return serviceTypeBySlug(x.filters.serviceTypeSlug);
        }
      ),
    getPetTypeServiceTypeOptions: (getState, createSelector) =>
      createSelector(
        [getState, serviceTypeSelectors.getPetTypeServiceTypes],
        (x, getPetTypeServiceTypes: (petTypes: PetType[]) => ServiceType[]) => {
          const petTypeServiceTypes = getPetTypeServiceTypes(x.filters.petType);

          const uniqueMapped = (services: ServiceType[]): SelectOption[] =>
            [...new Set(services)].map((s) => ({
              title: s.displayName,
              subtitle: s.description,
              value: s.slug,
            }));

          return uniqueMapped(petTypeServiceTypes);
        }
      ),
    getPetTypeBrowsableServiceTypeOptions: (getState, createSelector) =>
      createSelector(
        [getState, serviceTypeSelectors.getPetTypeBrowsableServiceTypes],
        (x, getPetTypeBrowsableServices: (petTypes: PetType[]) => ServiceType[]) => {
          const petTypeBrowsableServices = getPetTypeBrowsableServices(x.filters.petType);

          const uniqueMapped = (services: ServiceType[]): SelectOption[] =>
            [...new Set(services)].map((s) => ({
              title: s.displayName,
              subtitle: s.description,
              value: s.slug,
            }));

          return uniqueMapped(petTypeBrowsableServices);
        }
      ),
    getDateRangePickerLabel: (getState, createSelector, { getFilters }) =>
      createSelector(
        [getFilters, serviceTypeSelectors.getRepeatServiceTypes],
        ({ serviceTypeSlug }, repeatServiceTypes: ServiceType[]) => {
          const isRepeat = repeatServiceTypes.find((s) => s.slug === serviceTypeSlug);
          return isRepeat ? t`For what dates?` : t`For these days`;
        }
      ),
    getStartDatePlaceholder: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], ({ serviceTypeSlug }) => {
        const isDropOffPickUpSupported = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(serviceTypeSlug);
        return isDropOffPickUpSupported ? t`Drop off` : t`Start date`;
      }),
    getEndDatePlaceholder: (getState, createSelector, { getFilters }) =>
      createSelector([getFilters], ({ serviceTypeSlug }) => {
        const isDropOffPickUpSupported = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(serviceTypeSlug);
        return isDropOffPickUpSupported ? t`Pick up` : t`End date`;
      }),
    getIsDaycareFilterEarlierInExperiment: (getState, createSelector) =>
      createSelector([experimentsSelectors.isInExperimentGroup], (isInExperimentGroup) => {
        return (
          isInExperimentGroup(EXPERIMENTS.GINGR_DAYCARE_FILTER_EARLIER_EXPERIMENT, 'required') ||
          isInExperimentGroup(EXPERIMENTS.GINGR_DAYCARE_FILTER_EARLIER_EXPERIMENT, 'variant')
        );
      }),
    getIsDaycareFilterEarlierExperimentRequired: (getState, createSelector) =>
      createSelector([experimentsSelectors.isInExperimentGroup], (isInExperimentGroup) => {
        return isInExperimentGroup(EXPERIMENTS.GINGR_DAYCARE_FILTER_EARLIER_EXPERIMENT, 'required');
      }),
    getIsDaycareFilterOutsideMarketsInExperiment: (getState, createSelector) =>
      createSelector([experimentsSelectors.isInExperimentGroup], (isInExperimentGroup) => {
        return isInExperimentGroup(
          EXPERIMENTS.GINGR_DAYCARE_FILTER_OUTSIDE_MARKETS_EXPERIMENT,
          'variant'
        );
      }),
    getisInBoardingFilterEarlierExperiment: (getState, createSelector) =>
      createSelector([experimentsSelectors.isInExperimentGroup], (isInExperimentGroup) => {
        return isInExperimentGroup(EXPERIMENTS.GINGR_BOARDING_FILTER_EARLIER_EXPERIMENT, 'variant');
      }),
    getHasFilterError: (getState, createSelector) => createSelector([getState], (s) => s.hasError),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options);
